import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useForm, Controller } from "react-hook-form";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import api from "../../../services/api";

const ContextManagement = () => {
  const [visible, setVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [configType, setConfigType] = useState("text");
  const [selectedFile, setSelectedFile] = useState(null);
  const toast = useRef(null);
  const queryClient = useQueryClient();
  const fileUploadRef = useRef(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    defaultValues: {
      name: "",
      content: "",
      priority: 10,
      is_active: true
    }
  });

  const watchType = watch("type");

  // Fetch configurations
  const { data: configs, isLoading } = useQuery(
    "aiConfigurations",
    async () => {
      const response = await api.get("/ai/config");
      return response.data.data;
    },
    {
      refetchOnWindowFocus: false
    }
  );

  // Create text configuration
  const createTextMutation = useMutation(
    async (data) => {
      const response = await api.post("/ai/config/text", data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("aiConfigurations");
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Configuração criada com sucesso",
          life: 3000
        });
        handleClose();
      },
      onError: (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail:
            error?.response?.data?.message || "Erro ao criar configuração",
          life: 5000
        });
      }
    }
  );

  // Upload PDF configuration
  const uploadPDFMutation = useMutation(
    async (formData) => {
      const response = await api.post("/ai/config/pdf", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("aiConfigurations");
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "PDF enviado com sucesso",
          life: 3000
        });
        handleClose();
      },
      onError: (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: error?.response?.data?.message || "Erro ao enviar PDF",
          life: 5000
        });
      }
    }
  );

  // Update configuration
  const updateMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/ai/config/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("aiConfigurations");
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Configuração atualizada com sucesso",
          life: 3000
        });
        handleClose();
      },
      onError: (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail:
            error?.response?.data?.message || "Erro ao atualizar configuração",
          life: 5000
        });
      }
    }
  );

  // Toggle active status
  const toggleMutation = useMutation(
    async (id) => {
      const response = await api.post(`/ai/config/${id}/toggle`);
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries("aiConfigurations");
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: data.message,
          life: 3000
        });
      },
      onError: (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: error?.response?.data?.message || "Erro ao alterar status",
          life: 5000
        });
      }
    }
  );

  // Delete configuration
  const deleteMutation = useMutation(
    async (id) => {
      const response = await api.delete(`/ai/config/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("aiConfigurations");
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Configuração deletada com sucesso",
          life: 3000
        });
      },
      onError: (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail:
            error?.response?.data?.message || "Erro ao deletar configuração",
          life: 5000
        });
      }
    }
  );

  const handleClose = () => {
    setVisible(false);
    setEditMode(false);
    setCurrentId(null);
    setConfigType("text");
    setSelectedFile(null);
    reset();
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
  };

  const handleNew = () => {
    setEditMode(false);
    setConfigType("text");
    reset({
      name: "",
      content: "",
      priority: 10,
      is_active: true
    });
    setVisible(true);
  };

  const handleEdit = (rowData) => {
    if (rowData.type === "pdf") {
      toast.current?.show({
        severity: "warn",
        summary: "Aviso",
        detail:
          "Configurações de PDF não podem ser editadas. Delete e crie uma nova.",
        life: 5000
      });
      return;
    }

    setEditMode(true);
    setCurrentId(rowData.id);
    setConfigType(rowData.type);
    reset({
      name: rowData.name,
      content: rowData.content,
      priority: rowData.priority,
      is_active: rowData.is_active
    });
    setVisible(true);
  };

  const handleDelete = (rowData) => {
    confirmDialog({
      message: `Tem certeza que deseja deletar "${rowData.name}"?`,
      header: "Confirmar Exclusão",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      acceptClassName: "p-button-danger",
      accept: () => deleteMutation.mutate(rowData.id)
    });
  };

  const handleToggle = (rowData) => {
    toggleMutation.mutate(rowData.id);
  };

  const onSubmit = (data) => {
    if (configType === "text") {
      if (editMode) {
        updateMutation.mutate({ id: currentId, data });
      } else {
        createTextMutation.mutate(data);
      }
    } else if (configType === "pdf" && !editMode) {
      if (!selectedFile) {
        toast.current?.show({
          severity: "warn",
          summary: "Aviso",
          detail: "Por favor, selecione um arquivo PDF",
          life: 3000
        });
        return;
      }

      const formData = new FormData();
      formData.append("name", data.name);
      formData.append("file", selectedFile);
      formData.append("priority", data.priority || 10);
      formData.append("is_active", data.is_active ? 1 : 0);

      uploadPDFMutation.mutate(formData);
    }
  };

  const onFileSelect = (e) => {
    setSelectedFile(e.files[0]);
  };

  // Templates for DataTable
  const typeBodyTemplate = (rowData) => {
    return (
      <Tag
        value={rowData.type === "text" ? "Texto" : "PDF"}
        severity={rowData.type === "text" ? "info" : "danger"}
        icon={rowData.type === "text" ? "pi pi-file-edit" : "pi pi-file-pdf"}
      />
    );
  };

  const priorityBodyTemplate = (rowData) => {
    let severity = "success";
    let label = "Baixa";

    if (rowData.priority >= 50) {
      severity = "danger";
      label = "Alta";
    } else if (rowData.priority >= 20) {
      severity = "warning";
      label = "Média";
    }

    return <Tag value={label} severity={severity} />;
  };

  const statusBodyTemplate = (rowData) => {
    return (
      <Tag
        value={rowData.is_active ? "Ativo" : "Inativo"}
        severity={rowData.is_active ? "success" : "secondary"}
      />
    );
  };

  const actionsBodyTemplate = (rowData) => {
    return (
      <div style={{ display: "flex", gap: "8px" }}>
        {rowData.type === "text" && (
          <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-warning p-button-sm"
            onClick={() => handleEdit(rowData)}
            tooltip="Editar"
            tooltipOptions={{ position: "top" }}
          />
        )}
        <Button
          icon={rowData.is_active ? "pi pi-eye-slash" : "pi pi-eye"}
          className={`p-button-rounded p-button-sm ${
            rowData.is_active ? "p-button-secondary" : "p-button-success"
          }`}
          onClick={() => handleToggle(rowData)}
          tooltip={rowData.is_active ? "Desativar" : "Ativar"}
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-danger p-button-sm"
          onClick={() => handleDelete(rowData)}
          tooltip="Deletar"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  const dialogFooter = (
    <div className="form-actions">
      <Button
        label="Cancelar"
        icon="pi pi-times"
        onClick={handleClose}
        className="p-button-text"
      />
      <Button
        label={editMode ? "Atualizar" : "Criar"}
        icon="pi pi-check"
        onClick={handleSubmit(onSubmit)}
        loading={
          createTextMutation.isLoading ||
          uploadPDFMutation.isLoading ||
          updateMutation.isLoading
        }
        style={{ background: "#fac109", border: "none" }}
      />
    </div>
  );

  return (
    <div className="context-management">
      <Toast ref={toast} position="top-right" />
      <ConfirmDialog />

      <div className="context-header">
        <h3>
          <i className="pi pi-cog" style={{ marginRight: "10px" }} />
          Gerenciar Contextos da IA
        </h3>
        <Button
          label="Novo Contexto"
          icon="pi pi-plus"
          onClick={handleNew}
          style={{ background: "#0a073b", border: "none", color: "#fff" }}
        />
      </div>

      <DataTable
        value={configs}
        loading={isLoading}
        paginator
        rows={10}
        emptyMessage="Nenhuma configuração encontrada"
        className="context-table"
        stripedRows
      >
        <Column field="name" header="Nome" sortable />
        <Column
          field="type"
          header="Tipo"
          body={typeBodyTemplate}
          sortable
          style={{ width: "120px" }}
        />
        <Column
          field="priority"
          header="Prioridade"
          body={priorityBodyTemplate}
          sortable
          style={{ width: "130px" }}
        />
        <Column
          field="is_active"
          header="Status"
          body={statusBodyTemplate}
          sortable
          style={{ width: "110px" }}
        />
        <Column
          field="created_at"
          header="Criado em"
          sortable
          body={(rowData) =>
            new Date(rowData.created_at).toLocaleDateString("pt-BR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit"
            })
          }
          style={{ width: "180px" }}
        />
        <Column
          header="Ações"
          body={actionsBodyTemplate}
          style={{ width: "150px" }}
        />
      </DataTable>

      <Dialog
        header={editMode ? "Editar Contexto" : "Novo Contexto"}
        visible={visible}
        style={{ width: "600px" }}
        onHide={handleClose}
        footer={dialogFooter}
        className="context-form-dialog"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          {!editMode && (
            <div className="form-field">
              <label htmlFor="type">Tipo de Contexto *</label>
              <Dropdown
                id="type"
                value={configType}
                options={[
                  { label: "Texto", value: "text" },
                  { label: "PDF", value: "pdf" }
                ]}
                onChange={(e) => setConfigType(e.value)}
                placeholder="Selecione o tipo"
              />
            </div>
          )}

          <div className="form-field">
            <label htmlFor="name">Nome *</label>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Nome é obrigatório" }}
              render={({ field }) => (
                <InputText
                  id="name"
                  {...field}
                  placeholder="Ex: Regras de SLA"
                  className={errors.name ? "p-invalid" : ""}
                />
              )}
            />
            {errors.name && (
              <small className="form-error">{errors.name.message}</small>
            )}
          </div>

          {configType === "text" && (
            <div className="form-field">
              <label htmlFor="content">Conteúdo *</label>
              <Controller
                name="content"
                control={control}
                rules={{ required: "Conteúdo é obrigatório" }}
                render={({ field }) => (
                  <InputTextarea
                    id="content"
                    {...field}
                    rows={8}
                    placeholder="Digite o conteúdo do contexto..."
                    className={errors.content ? "p-invalid" : ""}
                  />
                )}
              />
              {errors.content && (
                <small className="form-error">{errors.content.message}</small>
              )}
              <small
                style={{ display: "block", color: "#6c757d", marginTop: "4px" }}
              >
                Máximo 10.000 caracteres
              </small>
            </div>
          )}

          {configType === "pdf" && !editMode && (
            <div className="form-field">
              <label>Arquivo PDF *</label>
              <FileUpload
                ref={fileUploadRef}
                mode="basic"
                accept="application/pdf"
                maxFileSize={10000000}
                onSelect={onFileSelect}
                chooseLabel="Escolher PDF"
                auto={false}
              />
              <small
                style={{ display: "block", color: "#6c757d", marginTop: "4px" }}
              >
                Máximo 10MB
              </small>
            </div>
          )}

          <div className="form-field">
            <label htmlFor="priority">Prioridade</label>
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <InputNumber
                  id="priority"
                  value={field.value}
                  onValueChange={(e) => field.onChange(e.value)}
                  min={0}
                  max={100}
                  placeholder="0-100"
                />
              )}
            />
            <small
              style={{ display: "block", color: "#6c757d", marginTop: "4px" }}
            >
              0-9: Baixa | 10-49: Média | 50-100: Alta
            </small>
          </div>

          <div className="form-field">
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    style={{ width: "18px", height: "18px", cursor: "pointer" }}
                  />
                )}
              />
              <label
                htmlFor="is_active"
                style={{ margin: 0, cursor: "pointer", userSelect: "none" }}
              >
                Ativo (usar no contexto da IA)
              </label>
            </div>
          </div>
        </form>
      </Dialog>
    </div>
  );
};

export default ContextManagement;
